import type { AccessibleFleet, Session } from '@cobliteam/common-types';
import type { AxiosError } from 'axios';
import decode, { type JwtPayload } from 'jwt-decode';
import snakecaseKeys from 'snakecase-keys';
import { CobliAxios } from '../../request/CobliAxios';
import HttpMethod from '../../request/HttpMethod';
import HttpStatusCode from '../../request/HttpStatusCode';
import type { Configuration, IRequest } from '../../request/Types';
import type { SessionAPI, UserInfoUpdateRequestAPI } from './ApiTypes';
import { AuthToken, deleteAuthCookies, setAuthCookies } from './AuthToken';
import { transformSession } from './legacySessionTransformer';

const parseAndStoreSession = (sessionAPI: SessionAPI): Session => {
  const session = transformSession(sessionAPI);
  setAuthCookies(session.id, session.authenticationToken);
  return session;
};

export class SessionManager {
  private axiosInstance: CobliAxios;

  constructor(configuration: Configuration, axiosInstance: CobliAxios = new CobliAxios(configuration)) {
    this.axiosInstance = axiosInstance;
  }

  private _handleKnownErrors(error: AxiosError) {
    const { response } = error;
    if (response) {
      // The request was made and the server responded with a status code

      const httpStatus = response.status;
      if (httpStatus === HttpStatusCode.PRECONDITION_REQUIRED) {
        const session = response.data as SessionAPI;
        return parseAndStoreSession(session);
      }
      if (httpStatus === HttpStatusCode.UNAUTHORIZED || httpStatus === HttpStatusCode.FORBIDDEN) {
        deleteAuthCookies();
      }
    }
    throw error;
  }

  public login = (rawUsername: string, password: string): Promise<Session> => {
    const username = rawUsername.trim().toLocaleLowerCase();
    const requestData: IRequest = {
      endpoint: '/account/authenticate',
      method: HttpMethod.POST,
      withCredentials: true,
      body: JSON.stringify({
        username,
        password,
      }),
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public tokenExchange = (idToken: string): Promise<Session> => {
    const requestData: IRequest = {
      endpoint: '/account/token-exchange',
      method: HttpMethod.POST,
      withCredentials: true,
      body: JSON.stringify({
        id_token: idToken,
      }),
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public logout = (): Promise<boolean> => {
    const requestData: IRequest = {
      endpoint: '/account/logout',
      method: HttpMethod.POST,
    };
    return this.axiosInstance
      .request(requestData)
      .then(deleteAuthCookies)
      .then(() => true);
  };

  private isAccessTokenExpired(jwtPayload: JwtPayload): boolean {
    return Date.now() >= ((jwtPayload.exp || 0) - 30) * 1000;
  }

  public maybeRenewAccessToken = (): Promise<Session | null> => {
    const encodedJWT = localStorage.getItem(AuthToken.ACCESS_TOKEN);
    const jwtPayload: null | JwtPayload = decode(encodedJWT || '');
    if (!jwtPayload || this.isAccessTokenExpired(jwtPayload)) {
      return this.getSession();
    }
    return Promise.resolve(null);
  };

  public getSession = (): Promise<Session> => {
    const requestData: IRequest = {
      endpoint: '/account/session',
      method: HttpMethod.GET,
      withCredentials: true,
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public update = (updates: UserInfoUpdateRequestAPI): Promise<Session> => {
    const requestData: IRequest = {
      endpoint: '/account/update',
      method: HttpMethod.POST,
      body: JSON.stringify(updates),
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public changeUserLoggedFleet = (userFleet: AccessibleFleet): Promise<Session> => {
    const requestData: IRequest = {
      endpoint: `/account/session/${userFleet.id}`,
      method: HttpMethod.POST,
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public getHubSpotSSOToken = async (): Promise<string> => {
    const requestData: IRequest = {
      endpoint: '/account/subscription-manager-token',
      method: HttpMethod.GET,
      withCredentials: true,
    };

    const response = await this.axiosInstance.request(requestData);
    const { jwt } = response.data;
    return jwt;
  };

  public getTemporaryToken = async (fleetId: string, fleetName: string): Promise<string> => {
    const requestData: IRequest = {
      endpoint: '/account/temporary-fleet-token',
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys({ fleetId, fleetName })),
    };

    const response = await this.axiosInstance.request(requestData);

    return response.data;
  };
}

export default SessionManager;
